/**
 * Utilidades para exportar datos a PDF y Excel
 */
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Exportar datos a Excel (.xlsx)
 * @param {Array} data - Array de objetos con los datos
 * @param {string} filename - Nombre del archivo (sin extensión)
 * @param {string} sheetName - Nombre de la hoja
 */
export const exportToExcel = (data, filename = 'reporte', sheetName = 'Datos') => {
  try {
    // Crear workbook y worksheet
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    
    // Ajustar ancho de columnas
    const maxWidth = 20;
    const colWidths = Object.keys(data[0] || {}).map(key => ({
      wch: Math.min(maxWidth, Math.max(key.length, 10))
    }));
    worksheet['!cols'] = colWidths;
    
    // Agregar hoja al workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    
    // Descargar archivo
    XLSX.writeFile(workbook, `${filename}.xlsx`);
    
    return true;
  } catch (error) {
    console.error('Error exportando a Excel:', error);
    throw error;
  }
};

/**
 * Exportar datos a PDF
 * @param {Array} data - Array de objetos con los datos
 * @param {string} filename - Nombre del archivo (sin extensión)
 * @param {string} title - Título del reporte
 * @param {Array} columns - Definición de columnas [{header, dataKey}]
 */
export const exportToPDF = (data, filename = 'reporte', title = 'Reporte', columns = null) => {
  try {
    const doc = new jsPDF();
    
    // Título
    doc.setFontSize(18);
    doc.setTextColor(40, 40, 40);
    doc.text(title, 14, 22);
    
    // Fecha de generación
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generado: ${new Date().toLocaleString('es-AR')}`, 14, 30);
    
    // Si no se especifican columnas, generarlas del primer objeto
    if (!columns && data.length > 0) {
      columns = Object.keys(data[0]).map(key => ({
        header: formatHeader(key),
        dataKey: key
      }));
    }
    
    if (!columns || columns.length === 0) {
      doc.text('No hay datos para mostrar', 14, 40);
      doc.save(`${filename}.pdf`);
      return true;
    }
    
    // Crear tabla manualmente
    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;
    const marginLeft = 14;
    const marginRight = 14;
    const contentWidth = pageWidth - marginLeft - marginRight;
    const cellHeight = 7;
    const colWidth = contentWidth / columns.length;
    let yPosition = 40;
    
    // Encabezados
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.setFillColor(66, 139, 202);
    
    columns.forEach((col, idx) => {
      const x = marginLeft + (idx * colWidth);
      doc.rect(x, yPosition - cellHeight + 1, colWidth, cellHeight, 'F');
      doc.text(col.header, x + 2, yPosition - 2, { maxWidth: colWidth - 4 });
    });
    
    yPosition += cellHeight;
    
    // Datos
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    let rowIndex = 0;
    
    data.forEach(row => {
      // Verificar si necesitamos nueva página
      if (yPosition > pageHeight - 20) {
        doc.addPage();
        yPosition = 20;
        
        // Repetir encabezados en nueva página
        doc.setFontSize(11);
        doc.setTextColor(255, 255, 255);
        doc.setFillColor(66, 139, 202);
        
        columns.forEach((col, idx) => {
          const x = marginLeft + (idx * colWidth);
          doc.rect(x, yPosition - cellHeight + 1, colWidth, cellHeight, 'F');
          doc.text(col.header, x + 2, yPosition - 2, { maxWidth: colWidth - 4 });
        });
        
        yPosition += cellHeight;
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
      }
      
      // Fondo alternado
      if (rowIndex % 2 === 0) {
        doc.setFillColor(245, 245, 245);
        doc.rect(marginLeft, yPosition - cellHeight + 1, contentWidth, cellHeight, 'F');
      }
      
      // Valores
      columns.forEach((col, idx) => {
        const x = marginLeft + (idx * colWidth);
        const value = formatValue(row[col.dataKey]);
        doc.text(value, x + 2, yPosition - 2, { maxWidth: colWidth - 4 });
      });
      
      yPosition += cellHeight;
      rowIndex++;
    });
    
    // Pie de página
    const pageCount = doc.internal.getNumberOfPages();
    doc.setFontSize(8);
    doc.setTextColor(150);
    
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.text(
        `Página ${i} de ${pageCount}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }
    
    // Descargar
    doc.save(`${filename}.pdf`);
    
    return true;
  } catch (error) {
    console.error('Error exportando a PDF:', error);
    throw error;
  }
};

/**
 * Formatear nombre de header (snake_case a Title Case)
 */
const formatHeader = (key) => {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Formatear valores para mostrar
 */
const formatValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (value instanceof Date) return value.toLocaleDateString('es-AR');
  return String(value);
};

/**
 * Exportar pacientes
 */
export const exportPacientes = (pacientes, format = 'excel') => {
  const data = pacientes.map(p => ({
    'Nombre': p.nombre,
    'Email': p.email || '',
    'Teléfono': p.telefono || '',
    'Fecha Nacimiento': p.fecha_nacimiento || '',
    'Estado': p.activo ? 'Activo' : 'Inactivo',
    'Notas': p.notas || ''
  }));
  
  if (format === 'excel') {
    exportToExcel(data, 'pacientes', 'Pacientes');
  } else {
    exportToPDF(data, 'pacientes', 'Listado de Pacientes');
  }
};

/**
 * Exportar turnos
 */
export const exportTurnos = (turnos, format = 'excel') => {
  const data = turnos.map(t => ({
    'Paciente': t.paciente_nombre || 'Sin asignar',
    'Fecha': new Date(t.fecha_hora).toLocaleDateString('es-AR'),
    'Hora': new Date(t.fecha_hora).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
    'Duración': `${t.duracion} min`,
    'Estado': t.estado || 'pendiente',
    'Notas': t.notas || ''
  }));
  
  if (format === 'excel') {
    exportToExcel(data, 'turnos', 'Turnos');
  } else {
    exportToPDF(data, 'turnos', 'Listado de Turnos');
  }
};

/**
 * Exportar pagos
 */
export const exportPagos = (pagos, format = 'excel') => {
  const data = pagos.map(p => ({
    'Paciente': p.paciente_nombre || 'Sin asignar',
    'Monto': `$${parseFloat(p.monto || 0).toFixed(2)}`,
    'Fecha': new Date(p.fecha_pago).toLocaleDateString('es-AR'),
    'Método': p.metodo_pago || '',
    'Estado': p.estado || ''
  }));
  
  if (format === 'excel') {
    exportToExcel(data, 'pagos', 'Pagos');
  } else {
    exportToPDF(data, 'pagos', 'Listado de Pagos');
  }
};

/**
 * Exportar reporte general
 */
export const exportReporteGeneral = (estadisticas, format = 'excel') => {
  const data = [
    { 'Métrica': 'Turnos Hoy', 'Valor': estadisticas.turnosHoy },
    { 'Métrica': 'Pacientes Activos', 'Valor': estadisticas.pacientesActivos },
    { 'Métrica': 'Ingresos del Mes', 'Valor': `$${estadisticas.ingresosMes}` },
    { 'Métrica': 'Turnos Completados', 'Valor': estadisticas.turnosCompletados },
    { 'Métrica': 'Tasa de Completado', 'Valor': `${estadisticas.tasaCompletado}%` },
    { 'Métrica': 'Turnos Cancelados', 'Valor': estadisticas.turnosCancelados },
    { 'Métrica': 'Tasa de Cancelación', 'Valor': `${estadisticas.tasaCancelacion}%` }
  ];
  
  if (format === 'excel') {
    exportToExcel(data, 'reporte-general', 'Estadísticas');
  } else {
    exportToPDF(data, 'reporte-general', 'Reporte General del Consultorio');
  }
};

export default {
  exportToExcel,
  exportToPDF,
  exportPacientes,
  exportTurnos,
  exportPagos,
  exportReporteGeneral
};
